import { Box } from "@mui/material";
import React from "react";
import theme from "../../theme";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "@geist-ui/icons";
import { Text } from "@geist-ui/core";
import PrimaryButton, {
  PrimaryButtonV2,
  SecondaryButton,
  SecondaryButtonV2,
} from "../input-elements/buttons";

interface MultistepFormButtonsProps {
  style?: React.CSSProperties;
  back: boolean;
  backFunction: () => void;
  disabled: boolean;
  nextButtonText?: string;
}

export default function MultistepFormButtons({
  style,
  back,
  disabled,
  nextButtonText,
  backFunction,
}: MultistepFormButtonsProps) {
  // Keeping original for compatibility if used elsewhere, focusing on V2 for the main flow
  return (
    <>
      <Box sx={{ display: { xs: "none", md: "flex" } }}>
        {back ? (
          <SecondaryButton
            style={{
              position: "sticky",
              width: "150px",
              margin: "40px 0px 0px 0px",
              marginRight: "10px",
            }}
            onClick={() => backFunction()}
          >
            Zurück
          </SecondaryButton>
        ) : null}
        <PrimaryButton
          disabled={disabled}
          style={{
            margin: "40px 0px 0px 0px",
            minWidth: "200px",
          }}
          type="submit"
        >
          {nextButtonText ? nextButtonText : "Weiter"}
        </PrimaryButton>
      </Box>
      <Box
        sx={{ display: { xs: "block", md: "none" } }}
        style={{
          position: "fixed",
          left: "0",
          bottom: "61px",
          width: "100vw",
          zIndex: 100,
          backgroundColor: theme.palette.background.paper,
          padding: "10px",
          ...style,
        }}
      >
        <PrimaryButton
          disabled={disabled}
          style={{
            borderRadius: "5px",
            width: "calc(100% - 20px)",
            height: "50px",
            margin: "0px",
            marginTop: "10px",
          }}
          type="submit"
        >
          {nextButtonText ? nextButtonText : "Weiter"}
        </PrimaryButton>
      </Box>
    </>
  );
}

export function MultistepFormButtonsV2({
  style,
  back,
  disabled,
  nextButtonText,
  backFunction,
}: MultistepFormButtonsProps) {
  return (
    <>
      {/* Desktop Navigation */}
      <div className="hidden md:flex items-center gap-8 mt-4" style={style}>
        {back && (
          <motion.div 
            whileHover={{ x: -4 }}
            className="flex items-center gap-2 cursor-pointer group"
            onClick={backFunction}
          >
            <ArrowLeft size={18} className="text-gray-400 group-hover:text-black transition-colors" />
            <Text span style={{ 
                fontSize: '14px', 
                fontWeight: 600, 
                color: '#666',
                fontFamily: '"Nunito Sans", sans-serif',
                letterSpacing: '0.01em'
            }} className="group-hover:text-black transition-colors">
                ZURÜCK
            </Text>
          </motion.div>
        )}
        
        <motion.button
            disabled={disabled}
            whileHover={!disabled ? { scale: 1.02, y: -2 } : {}}
            whileTap={!disabled ? { scale: 0.98 } : {}}
            type="submit"
            style={{
                background: disabled ? '#EEE' : '#000',
                color: disabled ? '#AAA' : '#fff',
                padding: '16px 40px',
                borderRadius: '100px',
                fontSize: '15px',
                fontWeight: 800,
                fontFamily: '"Nunito Sans", sans-serif',
                textTransform: 'uppercase',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                boxShadow: disabled ? 'none' : '0 10px 25px rgba(0,0,0,0.12)',
                minWidth: '240px',
                transition: 'all 0.3s ease'
            }}
        >
            {nextButtonText ? nextButtonText : "Weiter"}
            {!disabled && <ArrowRight size={20} />}
        </motion.button>
      </div>

      {/* Mobile Sticky Navigation */}
      <div
        className="block md:hidden"
        style={{
          position: "fixed",
          left: "0",
          bottom: "20px",
          width: "100vw",
          zIndex: 10000,
          padding: "20px",
          pointerEvents: "none",
          ...style,
        }}
      >
        <div className="flex items-center gap-4" style={{ pointerEvents: "auto" }}>
            {back && (
                <motion.div 
                    whileTap={{ scale: 0.95 }}
                    className="flex-shrink-0 w-12 h-12 rounded-full border border-gray-100 flex items-center justify-center bg-white"
                    onClick={backFunction}
                >
                    <ArrowLeft size={20} color="#000" />
                </motion.div>
            )}
            
            <motion.button
                disabled={disabled}
                whileTap={!disabled ? { scale: 0.97 } : {}}
                type="submit"
                style={{
                    flex: 1,
                    background: disabled ? '#EEE' : '#000',
                    color: disabled ? '#AAA' : '#fff',
                    padding: '16px',
                    borderRadius: '100px',
                    fontSize: '14px',
                    fontWeight: 900,
                    fontFamily: '"Nunito Sans", sans-serif',
                    textTransform: 'uppercase',
                    border: 'none',
                    textAlign: 'center',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                }}
            >
                {nextButtonText ? nextButtonText : "Weiter"}
                {!disabled && <ArrowRight size={18} />}
            </motion.button>
        </div>
      </div>
    </>
  );
}

